<template>
    <div class="animated fadeIn">
        <div class="kgr-row">
            <h3 class="kgr-headline kgr-headline--h3">
                {{ $t('Zeitraum der Rechnungsstellung') }}
            </h3>
        </div>
        <div class="kgr-inputGroup">
            <div class="kgr-grid kgr-grid--gutters kgr-grid--bigGutters">
                <div class="kgr-grid__cell">
                    <kgr-form-row
                        :label="$t('Start')"
                        :error="error"
                        field-name="start"
                    >
                        <kgr-date-picker
                            v-model="start"
                        />
                    </kgr-form-row>
                </div>
                <div class="kgr-grid__cell">
                    <kgr-form-row
                        :label="$t('Ende')"
                        :error="error"
                        field-name="end"
                    >
                        <kgr-date-picker
                            v-model="end"
                        />
                    </kgr-form-row>
                </div>
            </div>
        </div>
        <hr class="kgr-separator--light" />
        <div
            class="kgr-row"
        >
            <kgr-checkbox
                v-model="filters[0].checked"
                :label="$t('Offene Rechnungen berücksichtigen')"
            />
        </div>
        <div
            class="kgr-row"
        >
            <kgr-checkbox
                v-model="filters[1].checked"
                :label="$t('Teilbezahlte Rechnungen berücksichtigen')"
            />
        </div>
        <hr class="kgr-separator--light" />
        <div
            class="kgr-alert kgr-alert--info kgr-row"
        >
            <span class="kgr-text kgr-text--bold">
                <kgr-icon
                    icon-class="info-circle"
                    style="margin-bottom: -5px;margin-right:5px;color:black;"
                    disabled
                    small
                />{{ $t('Hinweis') }}</span><br />
            {{ $t('Es werden alle Rechnungen berücksichtigt, deren Rechnungssumme unter Berücksichtigung der bisher geleistete Zahlungen einen negativen Betrag, also eine Gutschrift, ausweisen.') }}
        </div>
        <div
            class="kgr-alert kgr-alert--info kgr-row kgr-postfix kgr-postfix--gutters kgr-postfix--top"
        >
            <div
                class="kgr-postfix__fluid"
            >
                <div
                    v-if="exportDisabled"
                >
                    <div class="kgr-row">
                        {{ availability.messages[0] }}
                    </div>
                    <ul
                        v-for="(message, index) in availability.messages"
                        :key="index"
                        class="kgr-list kgr-list--bulleted"
                    >
                        <li
                            v-if="index > 0"
                            v-html="message"
                        >
                        </li>
                    </ul>
                </div>
            </div>
            <div class="kgr-postfix__fixed">
                <button
                    class="kgr-button kgr-button--accent"
                    :disabled="exportDisabled"
                    @click="createSepaExport()"
                >
                    {{ $t('SEPA-Überweisungs-Export erstellen') }}
                </button>
            </div>
        </div>
        <hr class="kgr-separator--light" />
        <div class="kgr-row">
            <h2 class="kgr-headline kgr-headline--h2">
                {{ $t('SEPA-Überweisung-Exporte') }}
            </h2>
        </div>
        <table class="kgr-table kgr-table--block kgr-table--striped printable">
            <thead>
                <tr class="kgr-table__row">
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Export vom') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline kgr-table__cell--actions kgr-hidden-print"></th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="loading">
                    <td
                        colspan="4"
                        style="text-align: center;"
                    >
                        <kgr-loader />
                    </td>
                </tr>
                <tr v-else-if="sepaExports.length === 0">
                    <td
                        colspan="4"
                        style="text-align: center;"
                    >
                        {{ $t('dataTable.noData') }}
                    </td>
                </tr>
                <template v-else>
                    <tr
                        v-for="sepaExport in sepaExports"
                        :key="sepaExport.id"
                        class="kgr-table__row"
                    >
                        <td class="kgr-table__cell">
                            {{ formatDateTime(sepaExport.createdAt) }} <br />
                            {{ sepaExport.numberOfInvoices }} {{ $t('Rechnung(en)') }}
                        </td>
                        <td class="kgr-table__cell kgr-table__cell--actions kgr-hidden-print">
                            <button
                                class="kgr-table__action"
                                @click="download(sepaExport.id)"
                            >
                                <kgr-icon icon-class="save" />
                            </button>
                            <button
                                v-if="isDeletable && withinLastWeek(sepaExport)"
                                class="kgr-table__action"
                                @click="deleteExport(sepaExport.id)"
                            >
                                <kgr-icon icon-class="delete" />
                            </button>
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>
    </div>
</template>

<script>
import { format, parse, subDays } from '@legacy/js/date/date-fns.js';
import Checkbox from '../../../components/common/form/checkbox/Checkbox.vue';
import ClubRole from '../../../js/dto/ClubRole.js';
import ClubRoleRepository from '../../../js/repository/ClubRoleRepository.js';
import DatePicker from '../../../components/common/form/picker/DatePicker.vue';
import Error from '../../../js/model/Error.js';
import FormRow from '../../../components/common/form/FormRow.vue';
import Icon from '../../../components/common/icon/Icon.vue';
import Loading from '../../../components/common/Loading.vue';
import SepaCreditTransferExportAvailability from '../../../js/dto/SepaCreditTransferExportAvailability.js';
import SepaCreditTransferExportRepository from '../../../js/repository/SepaCreditTransferExportRepository.js';

export default {
    components: {
        'kgr-icon': Icon,
        'kgr-loader': Loading,
        'kgr-date-picker': DatePicker,
        'kgr-form-row': FormRow,
        'kgr-checkbox': Checkbox,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            error: new Error(),
            loading: true,
            loadingCreate: false,
            loadingAvailability: false,
            start: format(subDays(new Date(), 7), 'dd.MM.yyyy'),
            end: format(new Date(), 'dd.MM.yyyy'),
            checkOpenInvoices: true,
            checkUnequalInvoices: true,
            sepaExports: [],
            filters: [
                {status: 'open', checked: true, text: 'Offene Rechnungen berücksichtigen', icon: 'cached'},
                {status: 'unequal', checked: true, text: 'Teilbezahlte Rechnungen berücksichtigen ', icon: 'exposure'},
            ],
            availability: new SepaCreditTransferExportAvailability(),
            reloadExports: false,
            clubRoles: [],
        }
    },
    computed: {
        exportDisabled() {
            return this.loadingCreate
                || this.loadingAvailability
                || !this.availability.isAvailable
                || this.filters.filter(f => f.checked).length === 0;
        },
        isDeletable() {
            return this.clubRoles.length > 0 && (this.clubRoles.includes(ClubRole.ADMIN));
        },
    },
    watch: {
        'start': function () {
            this.getAvailability();
        },
        'end': function () {
            this.getAvailability();
        },
        'filters': {
            deep: true,
            handler() {
                this.getAvailability();
            }
        },
    },
    created() {
        this.loadClubRoles();
        this.loadSepaExports();
        this.getAvailability();
    },
    methods: {
        loadSepaExports() {
            this.loading = true;
            SepaCreditTransferExportRepository.findByClub(this.clubId)
                .then(response => {
                    this.sepaExports = response;
                    if (this.reloadExports) {
                        this.reloadExports = false;
                        this.getAvailability();
                    }
                })
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => this.loading = false);
        },
        createSepaExport() {
            this.loadingCreate = true;
            const filters = this.filters.filter(f => f.checked).map(f => {
                return {status: f.status};
            })
            const parsedStart = parse(this.start, 'dd.MM.yyyy', new Date());
            const formattedStart = format(parsedStart, 'yyyy-MM-dd');
            const parsedEnd = parse(this.end, 'dd.MM.yyyy', new Date());
            const formattedEnd = format(parsedEnd, 'yyyy-MM-dd');
            SepaCreditTransferExportRepository.createByClub(this.clubId, formattedStart, formattedEnd, {filters: filters})
                .then(() => {
                    this.loadSepaExports();
                    this.getAvailability();
                })
                .catch(() => this.$notify.error('saveChanges.error'))
                .finally(() => this.loadingCreate = false);
        },
        formatDateTime(date) {
            if (date === null) {
                return '';
            }
            const parsedDate = parse(date, 'yyyy-MM-dd HH:mm:ss', new Date());

            return format(parsedDate, 'dd.MM.yyyy HH:mm');
        },
        download(exportId) {
            SepaCreditTransferExportRepository.download(exportId)
                .then(() => {})
                .catch(() => this.$notify.error('error.loadingData'));
        },
        withinLastWeek(sepaExport) {
            const date = new Date();
            const dateMinus7Days = subDays(date, 7);
            return sepaExport.createdAt > format(dateMinus7Days, 'yyyy-MM-dd');
        },
        deleteExport(exportId) {
            SepaCreditTransferExportRepository.delete(exportId)
                .then(() => {
                    this.loadSepaExports();
                    this.getAvailability();
                })
                .catch(() => this.$notify.error('Daten konnten nicht gelöscht werden'));
        },
        getAvailability() {
            const filters = this.filters.filter(f => f.checked).map(f => f.status).join(',');
            if (filters.length === 0) {
                this.availability.isAvailable = false;
                this.availability.messages = [];
                return;
            }
            this.loadingAvailability = true;
            const parsedStart = parse(this.start, 'dd.MM.yyyy', new Date());
            const formattedStart = format(parsedStart, 'yyyy-MM-dd');
            const parsedEnd = parse(this.end, 'dd.MM.yyyy', new Date());
            const formattedEnd = format(parsedEnd, 'yyyy-MM-dd');
            const query = '?dateRange=' + formattedStart + ':' + formattedEnd + '&filters='+filters;
            SepaCreditTransferExportRepository.getAvailabilityByClubAndQuery(this.clubId, query)
                .then(response => {
                    this.availability = response;
                    if (this.availability.hasRunningExport) {
                        this.reloadExports = true;
                        setTimeout(() => this.getAvailability(), 5000)
                    } else if (this.reloadExports) {
                        this.loadSepaExports();
                    }
                })
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => this.loadingAvailability = false);
        },
        loadClubRoles() {
            ClubRoleRepository.findByClubAndCurrentUser(this.clubId)
                .then(response => response.map(clubRole => this.clubRoles.push(clubRole.name)));
        },
    },
}
</script>

<style scoped lang="scss" rel="stylesheet/scss">
</style>
